/**
 * useLoginWithPassword Hook
 *
 * Logs in with email and password using URQL mutation.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const LoginWithPasswordDocument = `
  mutation LoginWithPassword($email: String!, $password: String!) {
    loginWithPassword(email: $email, password: $password) {
      account {
        id
        telegramId
        firstName
        lastName
        username
        email
        hasPassword
      }
      tokens {
        accessToken
        refreshToken
        expiresAt
      }
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface AuthAccount {
  id: string;
  telegramId: string | null;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  email: string | null;
  hasPassword: boolean;
}

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
}

interface LoginResponse {
  account: AuthAccount;
  tokens: AuthTokens;
}

interface UseLoginWithPasswordResult {
  loginWithPassword: (email: string, password: string) => Promise<LoginResponse>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Log in with email and password.
 */
export function useLoginWithPassword(): UseLoginWithPasswordResult {
  const [result, executeMutation] = useMutation<
    { loginWithPassword: LoginResponse },
    { email: string; password: string }
  >(LoginWithPasswordDocument);

  const loginWithPassword = useCallback(
    async (email: string, password: string): Promise<LoginResponse> => {
      try {
        const response = await executeMutation({
          email: email.toLowerCase().trim(),
          password,
        });

        if (response.error) {
          throw new Error(
            response.error.graphQLErrors[0]?.message ||
              response.error.message ||
              "Failed to login"
          );
        }

        if (!response.data?.loginWithPassword) {
          throw new Error("No response from server");
        }

        errorTracking.addBreadcrumb({
          category: "auth",
          message: "Logged in with password",
        });

        return response.data.loginWithPassword;
      } catch (err) {
        const loginError =
          err instanceof Error ? err : new Error("Failed to login");

        errorTracking.captureError(loginError, {
          tags: { feature: "auth", action: "loginWithPassword" },
        });

        throw loginError;
      }
    },
    [executeMutation]
  );

  return {
    loginWithPassword,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
